/**
 * Entregados vs community map (Sol 15/09): a quién está cada compu entregada
 * — clapper activo, alguien dado de baja, un mail @winclap que no figura en el
 * map, un externo — y quién tiene más de una (duplicados). Los que no son
 * clapper activo se listan con modelo y serie para ir a mirarlos.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import type { DeliveredOwnerKind } from '@/../../shared/types';
import { DELIVERED_KIND_CLASSES } from './delivered-crosscheck-styles';

const KIND_ORDER: DeliveredOwnerKind[] = ['clapper', 'former', 'not_in_map', 'external', 'no_owner'];

export function DeliveredCrossCheckCard() {
  const { t } = useTranslation();
  const { data, isLoading } = trpc.inventory.getDeliveredCrossCheck.useQuery(undefined, { staleTime: 60_000 });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const k = 'devices.crosscheck';
  const toggle = (key: string) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-muted-foreground" />
          {t(`${k}.title`)}
          {data && <span className="text-sm font-normal text-muted-foreground">({t(`${k}.delivered_count`, { count: data.total })})</span>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t(`${k}.desc`)}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">…</p>}
        {data && !data.communityAvailable && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {t(`${k}.community_unavailable`)}
          </p>
        )}
        {data && data.communityAvailable && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {KIND_ORDER.map((kind) => (
                <div key={kind} className="rounded-lg border p-3">
                  <p className={cn('text-2xl font-bold tabular-nums', DELIVERED_KIND_CLASSES[kind])}>{data.counts[kind]}</p>
                  <p className="text-xs text-muted-foreground">{t(`${k}.kind.${kind}`)}</p>
                </div>
              ))}
            </div>

            {/* Listas: solo lo que no es clapper activo */}
            {KIND_ORDER.filter((kind) => kind !== 'clapper' && data.counts[kind] > 0).map((kind) => {
              const rows = data.items.filter((i) => i.kind === kind);
              return (
                <div key={kind} className="rounded-lg border">
                  <button type="button" onClick={() => toggle(kind)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm">
                    {open[kind] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <span className={cn('font-medium', DELIVERED_KIND_CLASSES[kind])}>{t(`${k}.kind.${kind}`)}</span>
                    <span className="text-muted-foreground">· {rows.length}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{t(`${k}.hint.${kind}`)}</span>
                  </button>
                  {open[kind] && (
                    <ul className="divide-y border-t text-sm">
                      {rows.map((r) => (
                        <li key={r.deviceId} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-1.5">
                          <span className="font-medium">{r.owner || '—'}</span>
                          {r.memberStatus && <span className="text-xs text-muted-foreground">({r.memberStatus})</span>}
                          <span className="text-muted-foreground">{r.modelo}</span>
                          <span className="font-mono text-xs text-muted-foreground">{r.nroSerie}</span>
                          {r.clapBase && <span className="text-xs text-muted-foreground">· {r.clapBase}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            {/* Duplicados */}
            <div className="rounded-lg border">
              <button type="button" onClick={() => toggle('dups')} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm">
                {open.dups ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                <span className={cn('font-medium', data.duplicates.length > 0 ? 'text-purple-700 dark:text-purple-300' : 'text-muted-foreground')}>
                  {t(`${k}.duplicates`)}
                </span>
                <span className="text-muted-foreground">· {data.duplicates.length}</span>
                <span className="ml-auto text-xs text-muted-foreground">{t(`${k}.duplicates_hint`)}</span>
              </button>
              {open.dups && (
                <ul className="divide-y border-t text-sm">
                  {data.duplicates.length === 0 && <li className="px-3 py-2 text-muted-foreground">{t(`${k}.no_duplicates`)}</li>}
                  {data.duplicates.map((d) => (
                    <li key={d.owner} className="px-3 py-1.5">
                      <span className="font-medium">{d.owner}</span>
                      <span className={cn('ml-2 text-xs', DELIVERED_KIND_CLASSES[d.kind])}>{t(`${k}.kind.${d.kind}`)}</span>
                      <ul className="mt-0.5 pl-4 text-xs text-muted-foreground">
                        {d.devices.map((x) => (
                          <li key={x.deviceId}>{x.modelo} · <span className="font-mono">{x.nroSerie}</span></li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
